import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import FormField from 'layouts/applications/wizard/components/FormField';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import Grid from '@mui/material/Grid';
import Swal from 'sweetalert2';
import SoftTypography from 'components/SoftTypography';
import { TextField, Checkbox, FormControlLabel } from '@mui/material';
import Cookies from 'js-cookie';

function ModalEnvioCobranza({ show, handleClose, getData, cobranza_id }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [para, setPara] = useState('');
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState(
    `Estimado cliente,\n\nAdjunto a este correo encontrarás la cobranza correspondiente.`
  );
  const [clientContacts, setClientContacts] = useState([]);

  // helper: arma headers de auth (cookie + bearer)
  const buildAuthHeaders = () => {
    const token =
      Cookies.get('access_token') || localStorage.getItem('access_token') || null;

    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  };

  // Lista fija de contactos de Cargonauts
  const contacts = [
    { email: 'andrea.arias@cargonauts.cl', name: 'Andrea Arias' },
    { email: 'claudio.vicuna@cargonauts.cl', name: 'Claudio Vicuña' },
    { email: 'gabriela@cargonauts.cl', name: 'Gabriela Riveros' },
    { email: 'informatica@cargonauts.cl', name: 'Informática' },
    { email: 'lorena@cargonauts.cl', name: 'Lorena Soto' },
    { email: 'jesus@cargonauts.cl', name: 'María Jesús' },
    { email: 'marta@cargonauts.cl', name: 'Marta Arias' },
    { email: 'omar@cargonauts.cl', name: 'Omar Quiroz' },
    { email: 'customer@cargonauts.cl', name: 'Customer' },
    { email: 'daisy@cargonauts.cl', name: 'Daisy' },
    { email: 'martina@cargonauts.cl', name: 'Martina' },
    { email: 'daniel@cargonauts.cl', name: 'Daniel' },
    { email: 'carla@cargonauts.cl', name: 'Carla' }
  ];

  // Construye o quita un email de la lista "para"
  const handleCheckboxChange = (email) => {
    setPara((prev) => {
      const list = prev.split(',').map(e => e.trim()).filter(Boolean);
      return list.includes(email)
        ? list.filter(e => e !== email).join(', ')
        : [...list, email].join(', ');
    });
  };

  // Carga datos del cliente al abrir
  useEffect(() => {
    if (!show || !cobranza_id) return;

    const load = async () => {
      try {
        const url = `${process.env.REACT_APP_SERVER_URL}/api/finance/send-email/${cobranza_id}/`;
        const res = await fetch(url, {
          method: 'GET',
          credentials: 'include',           // ← permite cookie cross-site en prod
          headers: {
            ...buildAuthHeaders(),          // ← Authorization: Bearer <token> si hay
          },
        });

        if (!res.ok) {
          const txt = await res.text();
          console.error('GET send-email failed:', res.status, txt);
          throw new Error(`Status ${res.status}`);
        }

        const data = await res.json();
        setPara(data.ema || '');
        setAsunto(`Cobranza #${data.num}`);

        const cc = [];
        if (data.cemail1) cc.push({ email: data.cemail1, name: data.cnombre1 });
        if (data.cemail2) cc.push({ email: data.cemail2, name: data.cnombre2 });
        if (data.cemail3) cc.push({ email: data.cemail3, name: data.cnombre3 });
        setClientContacts(cc);
      } catch (err) {
        console.error('Error cargando datos de cobranza:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text:
            err.message?.includes('401')
              ? 'Sesión expirada. Inicia sesión nuevamente.'
              : 'No se pudieron cargar los datos del cliente'
        });
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cobranza_id]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const payload = { asunto, destinatarios: para, mensaje };

    try {
      const url = `${process.env.REACT_APP_SERVER_URL}/api/finance/send-email/${cobranza_id}/`;
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...buildAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error('POST send-email failed:', res.status, txt);
        throw new Error(`Error ${res.status}`);
      }

      getData?.();
      Swal.fire({
        icon: 'success',
        title: 'Correo enviado',
        text: 'Correo enviado exitosamente',
      });
      handleClose?.();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          err.message?.includes('401')
            ? 'Sesión expirada. Inicia sesión nuevamente.'
            : `Hubo un problema al enviar el correo: ${err.message}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      className="modal-container small"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Envío Cobranza</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SoftBox mt={2}>
          <Grid container spacing={2}>
            {/* Para */}
            <Grid item xs={12}>
              <FormField
                type="text"
                label="Para"
                value={para}
                onChange={(e) => setPara(e.target.value)}
                placeholder="Correo electrónico del destinatario"
              />
            </Grid>

            {/* Contactos del cliente */}
            {clientContacts.length > 0 && (
              <Grid item xs={12}>
                <hr />
                <SoftTypography variant="h5" color="dark" mb={3}>
                  Contactos Cliente:
                </SoftTypography>
                <Grid container spacing={2}>
                  {clientContacts.map((contact, i) => {
                    const selected = para
                      .split(',')
                      .map(e => e.trim())
                      .filter(Boolean)
                      .includes(contact.email);
                    return (
                      <Grid item xs={12} sm={4} key={`${contact.email}-${i}`}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selected}
                              onChange={() => handleCheckboxChange(contact.email)}
                            />
                          }
                          label={contact.name || contact.email}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
                <hr />
              </Grid>
            )}

            {/* Contactos internos */}
            <Grid item xs={12}>
              <SoftTypography variant="h5" color="dark" mb={3}>
                Contactos Cargonauts:
              </SoftTypography>
              <Grid container spacing={2}>
                {contacts.map((contact, i) => {
                  const selected = para
                    .split(',')
                    .map(e => e.trim())
                    .filter(Boolean)
                    .includes(contact.email);
                  return (
                    <Grid item xs={12} sm={4} key={`${contact.email}-${i}`}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={selected}
                            onChange={() => handleCheckboxChange(contact.email)}
                          />
                        }
                        label={contact.name}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              <hr />
            </Grid>

            {/* Asunto */}
            <Grid item xs={12}>
              <FormField
                type="text"
                label="Asunto"
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                placeholder="Asunto del correo"
              />
            </Grid>

            {/* Mensaje */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                multiline
                rows={6}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Mensaje a enviar"
              />
            </Grid>
          </Grid>
        </SoftBox>
      </Modal.Body>
      <Modal.Footer>
        <SoftButton
          variant="gradient"
          color="success"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Enviando...' : 'Enviar'}
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="error"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancelar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
}

ModalEnvioCobranza.propTypes = {
  show: PropTypes.bool.isRequired,
  getData: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  cobranza_id: PropTypes.number.isRequired
};

export default ModalEnvioCobranza;
